import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";

import { CardLayout, MTable } from "../v3";
import { ProjectToolColumns } from "../../pages/project/constants";
import { ToolService } from "../../services";
import { derivePdfColumns, getPrintableRows } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ProjectTools = ({ storeId, reloadData }) => {
  const [tools, setTools] = useState([]);
  const [assignedTools, setAssignedTools] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "project-tools",
    defaults: { sortModel: [{ field: "updatedAt", sort: "desc" }] },
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(
    async (toolFilters = {}) => {
      setLoading(true);
      try {
        const { data: toolInventory } = await ToolService.getToolInventory({
          storeIds: [storeId],
          ...toolFilters,
        });
        const toolIds = toolInventory?.rows.map((tool) => tool.toolId);
        if (toolIds?.length > 0) {
          const { data: toolsResponse } = await ToolService.getTools({
            ids: toolIds.join(","),
            limit: toolIds.length,
            includeDeleted: true,
          });
          setTools(toolsResponse?.rows);
        }
        setAssignedTools(toolInventory?.rows);
        setTotalCount(toolInventory?.count);
      } catch (error) {
        toast.error("Failed to fetch assigned tools");
        console.error("Failed to fetch assigned tools");
      } finally {
        setLoading(false);
      }
    },
    [storeId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, searchText, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Tools" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Tools",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const columnHeaders = React.useMemo(() => ProjectToolColumns, []);

  const rows = assignedTools
    ?.filter((assignedTool) => assignedTool.quantity > 0)
    .map((assignedTool, index) => {
      const tool = tools.find((tool) => tool.id === assignedTool.toolId);
      return {
        id: assignedTool?.id,
        sl: index + 1 + paginationState.page * paginationState.pageSize,
        updatedAt: moment(assignedTool?.updatedAt).format("DD MMM YYYY"),
        tool: tool?.name,
        quantity: assignedTool?.quantity,
      };
    });
  return (
    <CardLayout>
      <MTable
        // Search data
        placeholder="Search tool"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        // showSearchBar
        // Export data
        showExportButton
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={columnHeaders}
        tableRows={rows}
        rowCount={totalCount}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
      />
    </CardLayout>
  );
};
